import { AxiosResponse } from "axios";
import apiService from "services/apiService";

// Shape returned by all config type APIs: { id, name }.
// Some endpoints also flag seeded system values that must not be deleted — the
// flag name is inconsistent across services (reimbursement → `default`,
// inventory → `isDefault`), so both are accepted. Use isSeededDefault() to read.
export interface ConfigItem {
  id: number;
  name: string;
  default?: boolean;
  isDefault?: boolean;
  // Some config lists (e.g. file types) carry an active flag. Consumers that
  // populate dropdowns should keep only active values.
  isActive?: boolean;
}

/** True when a config item is a protected, seeded system default. */
export const isSeededDefault = (item: ConfigItem): boolean =>
  item.default === true || item.isDefault === true;

export interface UpdateConfigOptions {
  // Send the update body as { names: [value] } instead of { name: value }.
  useNamesArray?: boolean;
}

// Per-module upload policy (GET/PUT api/upload-config/{MODULE}). `maxFileSizeMb`
// is a server-derived convenience; updates only send maxFiles + maxFileSizeKb.
export interface UploadConfig {
  module: string;
  maxFiles: number;
  maxFileSizeKb: number;
  maxFileSizeMb: number;
  allowedFileTypes: string[];
}

export interface UploadConfigUpdate {
  maxFiles: number;
  maxFileSizeKb: number;
}

/**
 * Centralised endpoints for every admin-configurable list. Import these wherever
 * a config list is fetched (incl. populating select dropdowns) so the paths are
 * declared in exactly one place and stay in sync with the config screens.
 */
export const ADMIN_CONFIG_ENDPOINTS = {
  reimbursementType: "api/reimbursement-types",
  reimbursementCurrencyType: "api/reimbursement-currency-types",
  employeeType: "api/employee/employee-types",
  designation: "api/employee/designations",
  assetType: "api/inventory/asset-types",
  deviceType: "api/inventory/device-types",
  deviceStatus: "api/inventory/device-statuses",
  // Allowed upload file types, configured per module.
  documentFileType: "api/employee/document-file-types",
  inventoryFileType: "api/inventory/file-types",
  reimbursementFileType: "api/reimbursement-file-types",
} as const;

/** Maps config items to react-select style options for dropdowns. */
export const toSelectOptions = (
  items: ConfigItem[] | undefined | null,
): { value: string; label: string }[] =>
  (items ?? []).map((item) => ({ value: item.name, label: item.name }));

class AdminConfigService {
  // Reusable GET for any config list — used by the config screens AND by any
  // dropdown that needs the same values (see ADMIN_CONFIG_ENDPOINTS).
  getAll(endpoint: string): Promise<AxiosResponse<ConfigItem[]>> {
    return apiService.get(endpoint);
  }

  add(endpoint: string, names: string[]): Promise<AxiosResponse<ConfigItem[]>> {
    return apiService.post(endpoint, { names });
  }

  update(
    endpoint: string,
    id: number,
    name: string,
    options?: UpdateConfigOptions,
  ): Promise<AxiosResponse<ConfigItem>> {
    const body = options?.useNamesArray ? { names: [name] } : { name };
    return apiService.put(`${endpoint}/${id}`, body);
  }

  remove(endpoint: string, id: number): Promise<AxiosResponse<void>> {
    return apiService.delete(`${endpoint}/${id}`);
  }

  // Convenience getters so dropdowns can fetch without hardcoding the path.
  getReimbursementTypes(): Promise<AxiosResponse<ConfigItem[]>> {
    return this.getAll(ADMIN_CONFIG_ENDPOINTS.reimbursementType);
  }

  getReimbursementCurrencyTypes(): Promise<AxiosResponse<ConfigItem[]>> {
    return this.getAll(ADMIN_CONFIG_ENDPOINTS.reimbursementCurrencyType);
  }

  getEmployeeTypes(): Promise<AxiosResponse<ConfigItem[]>> {
    return this.getAll(ADMIN_CONFIG_ENDPOINTS.employeeType);
  }

  getDesignations(): Promise<AxiosResponse<ConfigItem[]>> {
    return this.getAll(ADMIN_CONFIG_ENDPOINTS.designation);
  }

  getAssetTypes(): Promise<AxiosResponse<ConfigItem[]>> {
    return this.getAll(ADMIN_CONFIG_ENDPOINTS.assetType);
  }

  getDeviceTypes(): Promise<AxiosResponse<ConfigItem[]>> {
    return this.getAll(ADMIN_CONFIG_ENDPOINTS.deviceType);
  }

  getDeviceStatuses(): Promise<AxiosResponse<ConfigItem[]>> {
    return this.getAll(ADMIN_CONFIG_ENDPOINTS.deviceStatus);
  }

  // Allowed upload file types per module (for dropdowns / upload validation).
  getDocumentFileTypes(): Promise<AxiosResponse<ConfigItem[]>> {
    return this.getAll(ADMIN_CONFIG_ENDPOINTS.documentFileType);
  }

  getInventoryFileTypes(): Promise<AxiosResponse<ConfigItem[]>> {
    return this.getAll(ADMIN_CONFIG_ENDPOINTS.inventoryFileType);
  }

  getReimbursementFileTypes(): Promise<AxiosResponse<ConfigItem[]>> {
    return this.getAll(ADMIN_CONFIG_ENDPOINTS.reimbursementFileType);
  }

  // ── Upload policy (max files + max file size) per module ──────────────────
  // `endpoint` is the full module path, e.g. "api/upload-config/ASSET".
  getUploadConfig(endpoint: string): Promise<AxiosResponse<UploadConfig>> {
    return apiService.get(endpoint);
  }

  updateUploadConfig(
    endpoint: string,
    body: UploadConfigUpdate,
  ): Promise<AxiosResponse<UploadConfig>> {
    return apiService.put(endpoint, body);
  }
}

const adminConfigService = new AdminConfigService();
export default adminConfigService;
